import React, { useRef } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Drawer, LoadingSkeleton } from "@narmi/design_system";
import { useSetChatVisibility } from "cerulean";

import { useReceipt } from "../../hooks/useReceipt";

import ReceiptDetailsCard from "./ReceiptDetailsCard";
import ReceiptImagePreview from "./ReceiptImagePreview";
import SuggestedMatchCard from "./SuggestedMatchCard";
import UnmatchedEmptyCard from "./UnmatchedEmptyCard";
import styles from "./ReceiptDetailsDrawer.module.scss";

import type { ReceiptDetail } from "../../hooks/useReceipt";

interface MatchActionParams {
  suggestionId: number;
  receiptUuid: string;
}

interface CommonProps {
  uuid: string | null;
  pendingUuids: string[];
  onClose: () => void;
  onSelect: (uuid: string) => void;
  onAcceptMatch?: (params: MatchActionParams) => void;
  onRejectMatch?: (params: MatchActionParams) => void;
  pendingAcceptSuggestionId?: number | null;
  pendingRejectSuggestionId?: number | null;
}

type OpenDrawerProps = Omit<CommonProps, "uuid"> & { uuid: string };

const PendingFooter = ({
  receipt,
  onAcceptMatch,
  onRejectMatch,
  pendingAcceptSuggestionId,
  pendingRejectSuggestionId,
}: {
  receipt: ReceiptDetail;
  onAcceptMatch?: (params: MatchActionParams) => void;
  onRejectMatch?: (params: MatchActionParams) => void;
  pendingAcceptSuggestionId?: number | null;
  pendingRejectSuggestionId?: number | null;
}) => {
  const { l10n } = useLocalization();
  const suggestionId = receipt.suggestion?.id ?? null;
  const isAcceptable = suggestionId !== null && Boolean(onAcceptMatch);
  const isRejectable = suggestionId !== null && Boolean(onRejectMatch);
  const isAcceptPending =
    suggestionId !== null && pendingAcceptSuggestionId === suggestionId;
  const isRejectPending =
    suggestionId !== null && pendingRejectSuggestionId === suggestionId;
  const isAnyPending = isAcceptPending || isRejectPending;
  const handleAccept =
    isAcceptable && suggestionId !== null && onAcceptMatch
      ? () => onAcceptMatch({ suggestionId, receiptUuid: receipt.uuid })
      : undefined;
  const handleReject =
    isRejectable && suggestionId !== null && onRejectMatch
      ? () => onRejectMatch({ suggestionId, receiptUuid: receipt.uuid })
      : undefined;

  return (
    <div className={styles["footer-actions"]}>
      <Button
        kind="secondary"
        startIcon="x"
        label={l10n.getString("receipts-action-reject")}
        onClick={handleReject}
        disabled={!isRejectable || isAnyPending}
        isLoading={isRejectPending}
        aria-disabled={!isRejectable || isAnyPending ? "true" : undefined}
        testId="receipt-details-drawer-reject"
      />
      <Button
        kind="primary"
        startIcon="check"
        label={l10n.getString("receipts-action-accept")}
        onClick={handleAccept}
        disabled={!isAcceptable || isAnyPending}
        isLoading={isAcceptPending}
        aria-disabled={!isAcceptable || isAnyPending ? "true" : undefined}
        testId="receipt-details-drawer-accept"
      />
    </div>
  );
};

const MatchedFooter = () => {
  const { l10n } = useLocalization();
  return (
    <div className={styles["footer-actions"]}>
      <Button
        kind="secondary"
        label={l10n.getString("receipts-drawer-unlink")}
        onClick={() => {
          // TODO: wire to useUnlinkMatch (FINT-270 sub-task C4)
        }}
        testId="receipt-details-drawer-unlink"
      />
    </div>
  );
};

const UnmatchedFooter = ({
  downloadUrl,
  downloadFilename,
}: {
  downloadUrl: string;
  downloadFilename: string;
}) => {
  const { l10n } = useLocalization();
  return (
    <div className={styles["footer-actions"]}>
      <Button
        kind="secondary"
        as="a"
        href={downloadUrl}
        download={downloadFilename}
        label={l10n.getString("receipts-drawer-download")}
        testId="receipt-details-drawer-download"
      />
    </div>
  );
};

const PendingLayout = ({ receipt }: { receipt: ReceiptDetail }) => (
  <>
    <ReceiptDetailsCard
      merchant={receipt.merchant}
      transactionDate={receipt.transaction_date}
      amount={receipt.amount}
      uploadedBy={receipt.uploaded_by?.name ?? null}
      uploadedAt={receipt.uploaded_at}
    />
    {receipt.suggestion && (
      <SuggestedMatchCard suggestion={receipt.suggestion} variant="suggested" />
    )}
  </>
);

const MatchedLayout = ({ receipt }: { receipt: ReceiptDetail }) => (
  <>
    <ReceiptDetailsCard
      merchant={receipt.merchant}
      transactionDate={receipt.transaction_date}
      amount={receipt.amount}
      uploadedBy={receipt.uploaded_by?.name ?? null}
      uploadedAt={receipt.uploaded_at}
      approvedBy={receipt.approved_by?.name ?? null}
    />
    {receipt.suggestion && (
      <SuggestedMatchCard suggestion={receipt.suggestion} variant="matched" />
    )}
  </>
);

const UnmatchedLayout = ({ receipt }: { receipt: ReceiptDetail }) => (
  <>
    <ReceiptDetailsCard
      merchant={receipt.merchant}
      transactionDate={receipt.transaction_date}
      amount={receipt.amount}
      uploadedBy={receipt.uploaded_by?.name ?? null}
      uploadedAt={receipt.uploaded_at}
    />
    <UnmatchedEmptyCard receipt={receipt} />
  </>
);

const renderLayout = (receipt: ReceiptDetail) => {
  switch (receipt.status) {
    case "confirmed":
      return <MatchedLayout receipt={receipt} />;
    case "no_match":
    case "rejected":
    case "orphaned":
      return <UnmatchedLayout receipt={receipt} />;
    case "awaiting_review":
    default:
      return <PendingLayout receipt={receipt} />;
  }
};

const renderFooter = (
  receipt: ReceiptDetail,
  props: Pick<
    OpenDrawerProps,
    | "onAcceptMatch"
    | "onRejectMatch"
    | "pendingAcceptSuggestionId"
    | "pendingRejectSuggestionId"
  >
) => {
  switch (receipt.status) {
    case "confirmed":
      return <MatchedFooter />;
    case "no_match":
    case "rejected":
    case "orphaned":
      return (
        <UnmatchedFooter
          downloadUrl={receipt.download_url}
          downloadFilename={receipt.original_filename}
        />
      );
    case "awaiting_review":
    default:
      return <PendingFooter receipt={receipt} {...props} />;
  }
};

const OpenDrawer = ({
  uuid,
  pendingUuids,
  onClose,
  onSelect,
  onAcceptMatch,
  onRejectMatch,
  pendingAcceptSuggestionId,
  pendingRejectSuggestionId,
}: OpenDrawerProps) => {
  const { l10n } = useLocalization();
  // Hide the Glia "Let's talk!" chat bubble while the drawer is open — it
  // floats bottom-right and otherwise overlaps the Accept CTA in the footer.
  // OpenDrawer only mounts when the drawer is open, so the cleanup re-shows it.
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`
  );
  useSetChatVisibility(drawerIdRef.current, false);
  const { data, isLoading, isError, refetch } = useReceipt(uuid);

  const currentIndex = pendingUuids.indexOf(uuid);
  const prevUuid = currentIndex > 0 ? pendingUuids[currentIndex - 1] : null;
  const nextUuid =
    currentIndex >= 0 && currentIndex < pendingUuids.length - 1
      ? pendingUuids[currentIndex + 1]
      : null;

  const handlePrev = prevUuid ? () => onSelect(prevUuid) : null;
  const handleNext = nextUuid ? () => onSelect(nextUuid) : null;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div data-testid="receipt-details-drawer-loading">
          <LoadingSkeleton isLoading content="headerText" size="large" />
          <LoadingSkeleton isLoading content="paragraph" lines={4} />
        </div>
      );
    }

    if (isError || !data) {
      return (
        <div
          className={styles.error}
          data-testid="receipt-details-drawer-error"
        >
          <p className="fontColor--primary">
            {l10n.getString("receipts-drawer-error-load")}
          </p>
          <Button
            kind="secondary"
            label={l10n.getString("receipts-drawer-error-retry")}
            onClick={() => refetch()}
            testId="receipt-details-drawer-retry"
          />
        </div>
      );
    }

    return (
      <div className={styles["drawer-body"]}>
        <h2 className={`${styles.merchant} fontFamily--heading`}>
          {data.merchant ?? l10n.getString("receipts-table-unknown-merchant")}
        </h2>
        <ReceiptImagePreview
          imageUrl={data.image_url}
          mimeType={data.image_mime_type}
          thumbnailUrl={data.thumbnail_url}
          largeThumbnailUrl={data.large_thumbnail_url}
        />
        {renderLayout(data)}
      </div>
    );
  };

  const footer = data
    ? renderFooter(data, {
        onAcceptMatch,
        onRejectMatch,
        pendingAcceptSuggestionId,
        pendingRejectSuggestionId,
      })
    : null;

  return (
    <Drawer
      isOpen
      onUserDismiss={onClose}
      onPrev={handlePrev}
      onNext={handleNext}
      position="right"
      depth="580px"
      paddingSize="xl"
      footer={footer}
      testId="receipt-details-drawer"
    >
      {renderBody()}
    </Drawer>
  );
};

const ReceiptDetailsDrawer = ({
  uuid,
  pendingUuids,
  onClose,
  onSelect,
  onAcceptMatch,
  onRejectMatch,
  pendingAcceptSuggestionId,
  pendingRejectSuggestionId,
}: CommonProps) => {
  if (uuid === null) {
    return null;
  }

  return (
    <OpenDrawer
      uuid={uuid}
      pendingUuids={pendingUuids}
      onClose={onClose}
      onSelect={onSelect}
      onAcceptMatch={onAcceptMatch}
      onRejectMatch={onRejectMatch}
      pendingAcceptSuggestionId={pendingAcceptSuggestionId}
      pendingRejectSuggestionId={pendingRejectSuggestionId}
    />
  );
};

export default ReceiptDetailsDrawer;
